import { RESPONSE_METRICS_OPTIONS } from '../constants';

export type IncidentAnalyticsFilterParams = {
  teamID?: string;
  serviceOwner?: Array<string>;
  startDate?: string;
  endDate?: string;
  service?: Array<string>;
  assignee?: Array<string>;
  priority?: Array<string>;
  tags?: Array<string>;
  aggregateBy?: typeof RESPONSE_METRICS_OPTIONS[number];
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
};

export type OnCallUsersPerHourParams = {
  owner_id: string;
  search?: string;
  start_time: string;
  end_time: string;
  page_number: number;
  page_size: number;
  schedule_ids?: number[];
  schedule_owners?: string[];
};

export type OnCallUsersPerHourExportParams = {
  search?: string;
  schedule_ids?: number[];
  entity_owner?: string[];
  start_time?: string;
  end_time?: string;
};

export type IncidentAnalyticsTotalCountData = {
  currentCount: number;
  percentageChange: number;
  previuosCount: number;
};

export type IncidentAnalyticsTotalCount = {
  data: IncidentAnalyticsTotalCountData;
};

export type IncidentAnalyticsCountByTeam = {
  data: IncidentAnalyticsCountByTeamData;
};

export type IncidentAnalyticsCountByTeamData = {
  metrics: IncidentAnalyticsCountByTeamMetric[];
};

export type IncidentAnalyticsCountByTeamMetric = {
  count: number;
  teamID: string;
  teamName: string;
};

export type IncidentAnalyticsCountByStatus = {
  data: IncidentAnalyticsCountByStatusData;
};

export type IncidentAnalyticsCountByStatusData = {
  metrics: IncidentAnalyticsCountByStatusMetric[];
};

export type IncidentAnalyticsCountByStatusMetric = {
  count: number;
  status: string;
};

export type IncidentAnalyticsCountByPriority = {
  data: IncidentAnalyticsCountByPriorityData;
};

export type IncidentAnalyticsCountByPriorityData = {
  metrics: IncidentAnalyticsCountByPriorityMetric[];
};

export type IncidentAnalyticsCountByPriorityMetric = {
  count: number;
  priority: string;
};

export type IncidentAnalyticsCountByAssignee = {
  data: IncidentAnalyticsCountByAssigneeData;
};

export type IncidentAnalyticsCountByAssigneeData = {
  metrics: IncidentAnalyticsCountByAssigneeMetric[];
};

export type IncidentAnalyticsCountByAssigneeMetric = {
  assigneeID: string;
  assigneeName: string;
  count: number;
};

export type IncidentAnalyticsCountByServiceGroupByPriority = {
  data: IncidentAnalyticsCountByServiceGroupByPriorityData;
};

export type IncidentAnalyticsCountByServiceGroupByPriorityData = {
  metrics: IncidentAnalyticsCountByServiceGroupByPriorityMetric[];
};

export type IncidentAnalyticsCountByServiceGroupByPriorityMetric = {
  count: number;
  countByPriority: IncidentAnalyticsCountByServiceGroupByPriorityCount[];
  serviceID: string;
  serviceName: string;
};

export type IncidentAnalyticsCountByServiceGroupByPriorityCount = {
  count: number;
  priority: Priority;
};

export type IncidentAnalyticsCountByService = {
  data: IncidentAnalyticsCountByServiceData;
};

export type IncidentAnalyticsCountByServiceData = {
  metrics: IncidentAnalyticsCountByServiceMetric[];
};

export type IncidentAnalyticsCountByServiceMetric = {
  count: number;
  serviceID: string;
  serviceName: string;
};

export enum Priority {
  P1 = 'P1',
  P2 = 'P2',
  P3 = 'P3',
  P4 = 'P4',
  P5 = 'P5',
  Unset = 'unset',
}

export type Interval = 'hour' | 'day' | 'month' | 'week';

export type IncidentAnalyticsCountVsTimeByStatus = {
  data: IncidentAnalyticsCountVsTimeByStatusData;
};

export type IncidentAnalyticsCountVsTimeByStatusData = {
  interval: Interval;
  metrics: IncidentAnalyticsCountVsTimeByStatusMetric[];
};

export type IncidentAnalyticsCountVsTimeByStatusMetric = {
  countByStatus: IncidentAnalyticsCountVsTimeByStatusCount[];
  date: string;
};

export type IncidentAnalyticsCountVsTimeByStatusCount = {
  count: number;
  status: Status;
};

export enum Status {
  Acknowledged = 'acknowledged',
  Resolved = 'resolved',
  Triggered = 'triggered',
}

export type IncidentAnalyticsCountVsTimeByPriority = {
  data: IncidentAnalyticsCountVsTimeByPriorityData;
};

export type IncidentAnalyticsCountVsTimeByPriorityData = {
  interval: Interval;
  metrics: IncidentAnalyticsCountVsTimeByPriorityMetric[];
};

export type IncidentAnalyticsCountVsTimeByPriorityMetric = {
  countByPriority: IncidentAnalyticsCountVsTimeByPriorityCount[];
  date: Date;
};

export type IncidentAnalyticsCountVsTimeByPriorityCount = {
  count: number;
  priority: Priority;
};

export type IncidentAnalyticsCountVsTime = {
  data: IncidentAnalyticsCountVsTimeData;
};

export type IncidentAnalyticsCountVsTimeData = {
  interval: Interval;
  metrics: IncidentAnalyticsCountVsTimeMetric[];
};

export type IncidentAnalyticsCountVsTimeMetric = {
  count: number;
  date: Date;
};

export type IncidentAnalyticsFilterResponseData = {
  assignee: IncidentAnalyticsFilterResponseAssignee;
  priority: string[];
  retentionPeriod: number;
  service: IncidentAnalyticsFilterResponseService[];
  serviceOwnerMap: { [key: string]: string[] };
  startDate: Date;
  tags: IncidentAnalyticsFilterResponseTag[];
};

export type OnCallAnalyticsFilterResponseData = {
  assignee: IncidentAnalyticsFilterResponseAssignee;
  schedule: IncidentAnalyticsFilterResponseService[];
  scheduleOwnerMap: { [key: string]: string[] };
};

export type IncidentAnalyticsFilterResponseAssignee = {
  squads: IncidentAnalyticsFilterResponseSquad[];
  users: IncidentAnalyticsFilterResponseSquad[];
  youAndYourSquads: IncidentAnalyticsFilterResponseSquad[];
};

export type IncidentAnalyticsFilterResponseSquad = {
  assigneeType: IncidentAnalyticsFilterResponseAssigneeType;
  displayName: string;
  id: string;
  name: string;
};

export enum IncidentAnalyticsFilterResponseAssigneeType {
  Squad = 'squad',
  User = 'user',
}

export type IncidentAnalyticsFilterResponseService = {
  id: string;
  name: string;
};

export type IncidentAnalyticsFilterResponseTag = {
  key: string;
  values: Array<string>;
};

export type IncidentAnalyticsFilterResponse = {
  data: IncidentAnalyticsFilterResponseData;
};

export type OnCallAnalyticsFilterResponse = {
  data: OnCallAnalyticsFilterResponseData;
};

// ==== Respone Metrics TAB ====

export type IncidentAnalyticsXTTA = {
  data: IncidentAnalyticsXTTAData;
};

export type IncidentAnalyticsXTTAData = {
  aggregationType: string;
  current: number;
  percentageChange: number;
  previous: number;
};

export type IncidentAnalyticsXTTR = {
  data: IncidentAnalyticsXTTRData;
};

export type IncidentAnalyticsXTTRData = {
  aggregationType: string;
  current: number;
  percentageChange: number;
  previous: number;
};

export type IncidentAnalyticsXTTAVsTime = {
  data: IncidentAnalyticsXTTAVsTimeData;
};

export type IncidentAnalyticsXTTAVsTimeData = {
  aggregationType: string;
  interval: Interval;
  metrics: IncidentAnalyticsXTTAVsTimeMetric[];
};

export type IncidentAnalyticsXTTAVsTimeMetric = {
  date: Date;
  xtta: number;
};

export type IncidentAnalyticsXTTRVsTime = {
  data: IncidentAnalyticsXTTRVsTimeData;
};

export type IncidentAnalyticsXTTRVsTimeData = {
  interval: Interval;
  metrics: IncidentAnalyticsXTTRVsTimeMetric[];
};

export type IncidentAnalyticsXTTRVsTimeMetric = {
  date: Date;
  xttr: number;
};

export type IncidentAnalyticsXTTAByPriority = {
  data: IncidentAnalyticsXTTAByPriorityData;
};

export type IncidentAnalyticsXTTAByPriorityData = {
  aggregationType: string;
  metrics: IncidentAnalyticsXTTAByPriorityMetric[];
};

export type IncidentAnalyticsXTTAByPriorityMetric = {
  priority: string;
  xtta: number;
};

export type IncidentAnalyticsXTTRByPriority = {
  data: IncidentAnalyticsXTTRByPriorityData;
};

export type IncidentAnalyticsXTTRByPriorityData = {
  aggregationType: string;
  metrics: IncidentAnalyticsXTTRByPriorityMetric[];
};

export type IncidentAnalyticsXTTRByPriorityMetric = {
  priority: string;
  xttr: number;
};

export type IncidentAnalyticsXTTRByService = {
  data: IncidentAnalyticsXTTRByServiceData;
};

export type IncidentAnalyticsXTTRByServiceData = {
  aggregationType: string;
  metrics: IncidentAnalyticsXTTRByServiceMetric[];
};

export type IncidentAnalyticsXTTRByServiceMetric = {
  serviceID: string;
  serviceName: string;
  xttr: number;
};

export type IncidentAnalyticsXTTAByService = {
  data: IncidentAnalyticsXTTAByServiceData;
};

export type IncidentAnalyticsXTTAByServiceData = {
  aggregationType: string;
  metrics: IncidentAnalyticsXTTAByServiceMetric[];
};

export type IncidentAnalyticsXTTAByServiceMetric = {
  serviceID: string;
  serviceName: string;
  xtta: number;
};

export type TotalSilencedCount = {
  data: TotalSilencedCountData;
};

export type TotalSilencedCountData = {
  reductionPercentage: number;
  silencedCount: number;
  totalEvents: number;
};

export type NoiseReductionByService = {
  data: NoiseReductionByServiceData;
  metadata: {
    totalCount: number;
  };
};

export type NoiseReductionByServiceData = {
  metrics: NoiseReductionByServiceMetric[];
};

export type NoiseReductionByServiceMetric = {
  countByReductionType: NoiseReductionByServiceCountByReductionType[];
  reductionPercentage: number;
  serviceID: string;
  serviceName: string;
  triggeredCount: number;
};

export type NoiseReductionByServiceCountByReductionType = {
  count: number;
  reductionType: NoiseReductionByServiceReductionType;
};

export enum NoiseReductionByServiceReductionType {
  Apta = 'apta',
  Dedup = 'dedup',
  Iag = 'iag',
  Suppressed = 'suppressed',
}

export type APTAMetricsByService = {
  data: APTAMetricsByServiceData;
  metadata: {
    totalCount: number;
  };
};

export type APTAMetricsByServiceData = {
  metrics: APTAMetricsByServiceMetric[];
};

export type APTAMetricsByServiceMetric = {
  aptaFailureCount: number;
  aptaMarkedCount: number;
  aptaSuccessCount: number;
  nonTransientCount: number;
  reductionPercentage: number;
  serviceID: string;
  serviceName: string;
  totalIncidentCount: number;
};

export type NoiseReductionByTeam = {
  data: NoiseReductionByTeamData;
  metadata: Metadata;
};

export type OnCallUsersPerHour = {
  data: {
    onCallHours: OnCallUsersPerHourData[];
    totalCount: number;
  };
};

export type NoiseReductionByTeamData = {
  metrics: NoiseReductionByTeamMetric[];
};

export type NoiseReductionByTeamMetric = {
  countByReductionType: NoiseReductionByTeamCountByReductionType[];
  reductionPercentage: number;
  teamID: string;
  teamName: string;
  triggeredCount: number;
};

export type OnCallUsersPerHourData = {
  userID: string;
  fullName: string;
  userName: string;
  originalDuration: number;
  overrideDuration: number;
};

export type NoiseReductionByTeamCountByReductionType = {
  count: number;
  reductionType: NoiseReductionByTeamReductionType;
};

export enum NoiseReductionByTeamReductionType {
  Apta = 'apta',
  Dedup = 'dedup',
  Iag = 'iag',
  Suppressed = 'suppressed',
}

export type Metadata = {
  totalCount: number;
};
